import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Brain, GraduationCap } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { IMAGES, PRACTITIONER } from "@/data/site";

const QUALIFICATIONS = [
  "Master de psychologie clinique et psychopathologie",
  "Formation en psychothérapie intégrative",
  "Praticien EMDR (formation Europe)",
  "Praticien certifié en hypnose ericksonienne",
  "Formé à la PNL, à l'EFT et à la cohérence cardiaque",
  "Formation continue en mindfulness et thérapies brèves",
];

const VALUES = [
  {
    icon: Brain,
    title: "Rigueur clinique",
    text: "Une lecture clinique précise et une mise à jour continue des connaissances en psychothérapie et neurosciences appliquées.",
  },
  {
    icon: BookOpen,
    title: "Approche intégrative",
    text: "Mobilisation, selon les besoins, de méthodes complémentaires reconnues, dans un cadre thérapeutique structuré.",
  },
  {
    icon: GraduationCap,
    title: "Éthique professionnelle",
    text: "Respect strict du Code de déontologie, du secret professionnel et de l'autonomie de chaque patient.",
  },
];

export default function About() {
  return (
    <div data-testid="page-about" className="page-enter pt-32">
      <SEO
        title="À propos — François-Xavier Villette, psychologue Paris 16e"
        description="Découvrez le parcours, la formation et l'approche intégrative de François-Xavier Villette, psychologue et psychothérapeute à Paris 16e."
      />

      <section className="container-fxv py-16 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="overline gold-line">Le praticien</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-serif text-5xl md:text-6xl text-[#0C2340] mt-6 leading-[1.05]">
              François-Xavier <em className="not-italic text-[#C5A059]">Villette</em>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#4A5568] mt-6 text-lg leading-relaxed">
              Psychologue et psychothérapeute, j'accompagne adultes et
              professionnels confrontés à des situations d'anxiété, de
              burn-out, de psychotraumatisme ou de transitions de vie
              importantes. Mon approche allie rigueur scientifique et
              méthodes intégratives validées.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <img
              src={IMAGES.portrait}
              alt="François-Xavier Villette"
              className="w-full aspect-[5/6] object-cover"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F0F4F8] section-fxv">
        <div className="container-fxv grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline gold-line">Approche</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6">
              Une lecture intégrative au service de votre singularité
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-[#4A5568] leading-relaxed text-lg space-y-6">
            <p>
              La pratique intégrative repose sur une conviction : aucune
              méthode unique ne saurait répondre à toutes les souffrances. Mon
              travail consiste à choisir, à chaque étape, les outils
              thérapeutiques les plus pertinents pour vous.
            </p>
            <p>
              Cette approche combine écoute clinique, lecture
              psychodynamique, outils des thérapies brèves (EMDR, hypnose,
              EFT, PNL) et pratiques corps-esprit (cohérence cardiaque,
              mindfulness). Le tout dans un cadre strictement éthique et
              respectueux.
            </p>
            <p>
              Les consultations se déroulent en cabinet à Paris 16e ou en
              visioconsultation sécurisée. Elles s'adressent aux adultes
              francophones en demande d'un accompagnement professionnel.
            </p>
          </div>
        </div>
      </section>

      <section className="section-fxv bg-white">
        <div className="container-fxv">
          <div className="max-w-2xl">
            <p className="overline gold-line">Valeurs</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6">
              Trois piliers fondamentaux
            </h2>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="bg-white p-10 h-full">
                  <v.icon size={28} strokeWidth={1.5} className="text-[#C5A059]" />
                  <h3 className="font-serif text-2xl text-[#0C2340] mt-6">
                    {v.title}
                  </h3>
                  <p className="text-[#4A5568] mt-4 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-fxv bg-[#0C2340] text-white">
        <div className="container-fxv grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="overline">
              <span className="gold-line">Formations & qualifications</span>
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mt-6 text-white">
              Une formation continue, exigeante et plurielle
            </h2>
            <p className="text-[#C9D4E3] mt-6 leading-relaxed">
              La psychothérapie est un métier qui se nourrit d'apprentissage
              permanent. Chaque approche complémentaire fait l'objet d'une
              formation certifiée et d'une pratique supervisée.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="divide-y divide-[#1A3454] border-y border-[#1A3454]">
              {QUALIFICATIONS.map((q) => (
                <li
                  key={q}
                  className="py-5 flex items-start gap-4 text-[#C9D4E3]"
                >
                  <span className="text-[#C5A059] font-serif text-2xl leading-none mt-0.5">
                    ·
                  </span>
                  <span>{q}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#C5A059] text-[#0C2340] px-7 py-3.5 text-sm font-medium hover:bg-[#D4B374] transition-colors"
            >
              Prendre rendez-vous
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
